<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

interface SystemWarning {
  id: number;
  level: 'notice' | 'warning' | 'failure';
  message: string;
  starts_at: string;
  ends_at: string | null;
}

const warning = ref<SystemWarning | null>(null);
const bannerRef = ref<HTMLElement | null>(null);

const levelColors: Record<SystemWarning['level'], string> = {
  notice: '#3B82F6',
  warning: '#F59E0B',
  failure: '#EF4444',
};

function updateBannerHeight() {
  if (bannerRef.value) {
    const height = bannerRef.value.offsetHeight;
    document.documentElement.style.setProperty('--system-banner-height', `${height}px`);
  }
}

function clearBannerHeight() {
  document.documentElement.style.removeProperty('--system-banner-height');
}

onMounted(async () => {
  const adminApi = import.meta.env.VITE_APP_ADMIN_API;
  if (!adminApi) return;

  try {
    const response = await fetch(`${adminApi}/system-warning/students`, {
      signal: AbortSignal.timeout(5000),
    });

    if (response.ok) {
      const data = await response.json();
      warning.value = data.warning || null;

      if (warning.value) {
        await nextTick();
        updateBannerHeight();
      }
    }
  } catch (e) {
    console.error('Failed to fetch system warning:', e);
  }
});

onBeforeUnmount(() => {
  clearBannerHeight();
});
</script>

<template>
  <div
    v-if="warning"
    ref="bannerRef"
    class="system-warning-banner"
    :style="{ backgroundColor: levelColors[warning.level] }"
  >
    <i class="fa fa-exclamation-triangle mr-2"></i>
    {{ warning.message }}
  </div>
</template>

<style scoped>
.system-warning-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1050;
  padding: 12px 20px;
  text-align: center;
  font-size: 14px;
  color: #fff;
}

@media print {
  .system-warning-banner {
    display: none !important;
  }
}
</style>
